import React, { ReactNode, useMemo } from 'react'
import { ThemeProvider, createTheme } from '@mui/material'
import { theme as baseTheme } from '../../theme'
import { ResolvedRmnPalette } from '../../types/rmn'
import { mixHex, contrastText } from '../../utils/color'

interface Props {
  palette: ResolvedRmnPalette
  children: ReactNode
}

// Scoped theming for the /portal/:brandSlug subtree: a per-brand MUI theme for
// inputs/buttons/alerts plus CSS custom properties consumed by the tool
// pages' theme constants (var(--rmn-*, <TDC fallback>)). Outside this
// wrapper the vars are unset, so every existing page renders unchanged.
export function RmnThemeProvider({ palette, children }: Props) {
  const rmnTheme = useMemo(
    () =>
      createTheme(baseTheme, {
        palette: {
          primary: {
            main: palette.primary,
            light: mixHex(palette.primary, '#ffffff', 0.25),
            dark: palette.primaryDark,
            contrastText: contrastText(palette.primary),
          },
          secondary: {
            main: palette.secondary,
            contrastText: contrastText(palette.secondary),
          },
        },
      }),
    [palette]
  )

  const cssVars = {
    '--rmn-primary': palette.primary,
    '--rmn-primary-dark': palette.primaryDark,
    '--rmn-primary-tint': palette.primaryTint,
    '--rmn-secondary': palette.secondary,
    '--rmn-bg': palette.background,
    minHeight: '100vh',
    background: 'var(--rmn-bg)',
    display: 'flex',
    flexDirection: 'column',
  } as React.CSSProperties

  return (
    <ThemeProvider theme={rmnTheme}>
      <div style={cssVars}>{children}</div>
    </ThemeProvider>
  )
}
